import { Color, PointLight, Vector3, type Camera, type Object3D } from 'three';

/**
 * THE BUDGET FOR LIGHTS THAT MOVE.
 *
 * Everything bolted to the building is baked into the geometry — see
 * `bakeStaticLight`, which also has the measurements explaining why. What is
 * left over is the small set of lights that cannot be baked because they follow
 * something: a thief's torch, a guard's lamp, an alarm strobe, the flash of a
 * cutting tool. Those have to be real, and real lights in a forward renderer are
 * expensive in a way that has nothing to do with where they are — three.js
 * evaluates every light in the scene for every lit fragment on screen, so a
 * torch two rooms away costs exactly what a torch in your hand costs.
 *
 * So the game does not get to create lights. It registers SOURCES, which are
 * data, and this hands out a fixed number of real lights to whichever sources
 * matter most from where the camera is standing. A round with ten players
 * holding torches renders four of them — the four you could actually see.
 *
 * WHY THE POOL IS A FIXED SIZE. Three.js bakes the light count into every shader
 * it compiles and recompiles the lot when the count changes. Creating and
 * destroying lights as players switch torches on would stall for hundreds of
 * milliseconds each time. The slots are made once at boot and never added to or
 * removed; they only move, change colour and change brightness, none of which
 * touches the shader.
 */

/**
 * How long a slot takes to hand over, in seconds.
 *
 * Without this the swap is a visible click. Fading through zero costs nothing
 * and makes the handover unnoticeable, because a light that is changing hands is
 * by definition one of the least important on screen.
 */
const FADE = 0.22;

/**
 * Bonus applied to a source that already holds a slot, when ranking.
 *
 * Two torches of near-equal score sitting either side of the cut-off will
 * otherwise trade the slot back and forth every frame, and the room flickers.
 * Incumbency has to be worth something.
 */
const HYSTERESIS = 1.4;

/**
 * A light the game owns. Move it by writing to `position`; the director reads it
 * every frame. Set `intensity` to zero to switch it off without deregistering.
 */
export interface LightSource {
  position: Vector3;
  color: Color;
  intensity: number;
  /** Reach in metres. Also the culling radius, so it must be finite. */
  distance: number;
  decay: number;
}

interface Held {
  src: LightSource;
  /** Slot index currently rendering this source, or -1. */
  slot: number;
}

interface Slot {
  light: PointLight;
  held: Held | null;
  /** Source waiting for this slot to finish fading out. */
  incoming: Held | null;
  /** 0..1 fade envelope. */
  level: number;
}

export interface LightDirectorStats {
  sources: number;
  budget: number;
  active: number;
}

export class LightDirector {
  private readonly sources: Held[] = [];
  private readonly slots: Slot[] = [];
  private readonly scratch = new Vector3();

  constructor(attachTo: Object3D, budget: number) {
    for (let i = 0; i < budget; i++) {
      // Intensity 0 rather than absent: the light must exist from the first
      // frame so every shader is compiled once, at the final light count.
      const light = new PointLight(0xffffff, 0, 1, 2);
      light.castShadow = false;
      attachTo.add(light);
      this.slots.push({ light, held: null, incoming: null, level: 0 });
    }
  }

  /** Start considering this source. Returns a handle to stop again. */
  add(src: LightSource): () => void {
    const held: Held = { src, slot: -1 };
    this.sources.push(held);
    return () => {
      const i = this.sources.indexOf(held);
      if (i < 0) return;
      this.sources.splice(i, 1);
      for (const slot of this.slots) {
        if (slot.held === held) slot.held = null;
        if (slot.incoming === held) slot.incoming = null;
      }
    };
  }

  /**
   * Score a source for the camera's current position.
   *
   * Inverse-square on distance, matching how the light actually falls off, and
   * hard zero beyond its reach so a torch in the car park can never outrank one
   * two metres away just for being bright.
   */
  private score(h: Held, eye: Vector3): number {
    if (h.src.intensity <= 0) return 0;
    const d = this.scratch.copy(h.src.position).sub(eye).length();
    if (d > h.src.distance) return 0;
    const base = h.src.intensity / (1 + d * d * 0.06);
    return h.slot >= 0 ? base * HYSTERESIS : base;
  }

  update(camera: Camera, dt: number): void {
    if (!this.slots.length) return;
    const eye = camera.getWorldPosition(new Vector3());

    const ranked = this.sources
      .map((h) => ({ h, v: this.score(h, eye) }))
      .filter((x) => x.v > 0)
      .sort((a, b) => b.v - a.v)
      .slice(0, this.slots.length)
      .map((x) => x.h);
    const wanted = new Set(ranked);

    // Retire slots whose source has dropped out of the set. They fade first and
    // are only reassigned once dark, so no slot ever jump-cuts between sources.
    for (const slot of this.slots) {
      if (slot.held && !wanted.has(slot.held)) {
        slot.held.slot = -1;
        slot.held = null;
      }
    }

    const homeless = ranked.filter((h) => h.slot < 0);
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i];
      if (slot.held || slot.incoming) continue;
      const next = homeless.pop();
      if (!next) break;
      // Claim it now, so the next frame's ranking sees it as an incumbent and
      // does not hand the same source to a second slot as well.
      next.slot = i;
      slot.incoming = next;
    }

    for (const slot of this.slots) {
      const target = slot.held ? 1 : 0;
      const step = dt / FADE;
      slot.level =
        target > slot.level ? Math.min(target, slot.level + step) : Math.max(target, slot.level - step);

      // Dark and waiting: adopt whoever was queued for this slot.
      if (slot.level <= 0.001 && slot.incoming) {
        slot.held = slot.incoming;
        slot.incoming = null;
      }

      if (slot.held) {
        // Copied every frame, not on handover: these sources move.
        slot.light.position.copy(slot.held.src.position);
        slot.light.color.copy(slot.held.src.color);
        slot.light.distance = slot.held.src.distance;
        slot.light.decay = slot.held.src.decay;
        slot.light.intensity = slot.held.src.intensity * slot.level;
      } else {
        slot.light.intensity = 0;
      }
    }
  }

  /**
   * Re-pick and light up immediately, with no cross-fade.
   *
   * For teleports — the fixed review cameras, a respawn, a chute drop. The fade
   * exists to hide a handover as the camera moves between two places; when the
   * camera has simply appeared somewhere else there is nothing to hide, and
   * easing in over a fifth of a second means the screenshot harness photographs
   * every room half-lit.
   */
  snap(camera: Camera): void {
    for (const slot of this.slots) {
      if (slot.held) slot.held.slot = -1;
      slot.held = null;
      slot.incoming = null;
      slot.level = 0;
    }
    // Two passes of a full fade: the first assigns, the second brings the
    // envelope to one.
    this.update(camera, FADE);
    this.update(camera, FADE);
  }

  get stats(): LightDirectorStats {
    return {
      sources: this.sources.length,
      budget: this.slots.length,
      active: this.slots.filter((s) => s.held !== null).length,
    };
  }
}
